/**
 * Affiliate Postback Panel
 * ────────────────────────
 * Per-page configuration UI for the S2S postback flow.
 *
 * What the operator sees:
 *   1. Enable toggle.
 *   2. Two paste-ready postback URLs (Registration + FTD/Purchase)
 *      with one-click copy buttons. These get pasted into the destination
 *      site's affiliate platform "Postback URL" field.
 *   3. Click-id placeholder helper — shows the exact string to paste
 *      INTO the Link component's URL where the destination site's tracking
 *      expects the affiliate sub-id.
 *   4. Default value/currency for events that don't carry money data
 *      (most registrations, some platforms).
 *   5. Recent postback log so the operator can verify in-place that
 *      conversions are landing.
 *   6. Rotate-secret button (in case a URL leaks).
 *
 * Why a separate component (not inline in Builder.tsx):
 *   - Keeps the analytics step compact.
 *   - Reused later in the Dashboard page for at-a-glance stats.
 */

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ToggleSwitch } from './ToggleSwitch';
import {
  Copy,
  RefreshCcw,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Send,
  ListChecks,
} from 'lucide-react';

// IMPORTANT: VITE_API_URL is configured WITH the `/api` suffix in the
// rest of the codebase (e.g. `https://api.hitzlink.com/api`). Other
// callers concatenate as `${API_URL}/pages`, `${API_URL}/capi/...`.
// Concatenating `${API_URL}/api/capi/...` here produced double `/api`
// in production → 404. Default fallback also includes `/api` so local
// dev "just works" without VITE_API_URL override.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface PostbackUrls {
  registration: string;
  ftd: string;
  purchase: string;
  custom: string;
}

interface AffiliateConfig {
  enabled: boolean;
  postbackSecret: string | null;
  clickIdPlaceholder: string;
  defaultCurrency: string;
  defaultRegistrationValue: number;
  defaultPurchaseValue: number;
  eventMap: Record<string, string> | null;
  postbackUrls: PostbackUrls | null;
}

interface PostbackRow {
  clickId: string;
  clickedAt: string;
  postbacks: Array<{
    receivedAt: string;
    event: string;
    rawEvent?: string;
    value?: number | null;
    currency?: string | null;
    capiOk?: boolean;
    capiError?: string | null;
  }>;
  latest: {
    event: string;
    value?: number | null;
    currency?: string | null;
    capiOk?: boolean;
    capiError?: string | null;
    receivedAt: string;
  };
}

// ── Phase 9: outbound tracker postback ──────────────────────────────
type OutboundEventsEnabled = Record<string, boolean>;

interface OutboundConfig {
  enabled: boolean;
  preset: string;
  trackerDomain: string;
  postbackUrlTemplate: string;
  defaultPayout: number;
  defaultCurrency: string;
  macroOverrides: Record<string, string>;
  eventsEnabled: OutboundEventsEnabled;
}

interface OutboundPreset {
  id: string;
  displayName: string;
  clickIdParamCandidates: string[];
  postbackUrlTemplate: string;
  requiredMacros: string[];
  defaultMacros: Record<string, string>;
  notes: string;
}

interface OutboundLogRow {
  id: string;
  page_id: string;
  click_id: string | null;
  tracker_click_id: string | null;
  tracker_preset: string;
  event_name: string;
  payout: number | null;
  currency: string | null;
  txid: string | null;
  url: string;
  http_status: number | null;
  response_body: string | null;
  attempt_count: number;
  status: 'success' | 'failed' | 'pending' | 'skipped';
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

interface OutboundTestResult {
  ok: boolean;
  url: string;
  httpStatus: number | null;
  body: string | null;
  durationMs: number;
  error: string | null;
}

const OUTBOUND_EVENT_KEYS: Array<{ key: string; labelKey: string }> = [
  { key: 'lead', labelKey: 'lead' },
  { key: 'registration', labelKey: 'registration' },
  { key: 'ftd', labelKey: 'ftd' },
  { key: 'purchase', labelKey: 'purchase' },
];

interface Props {
  pageId: string;
  /**
   * When this panel is rendered inside a wrapper that already provides
   * its own collapsible header (e.g. the Builder's TrackingSection),
   * pass `embedded` to suppress the duplicate "Affiliate Postback
   * Tracking" title and description block.
   */
  embedded?: boolean;
  /**
   * Builder can promote the per-page enabled switch to the parent
   * TrackingSection row. Keep the standalone tracker page unchanged.
   */
  hideEnableControl?: boolean;
}

const PLACEHOLDER_PRESETS: Array<{ label: string; value: string; hintKey: 'default' | 'subid' | 'sub_id' | 'subidupper' | 'aff_sub' | 's1' }> = [
  { label: '{click_id}',  value: '{click_id}',  hintKey: 'default' },
  { label: '{subid}',     value: '{subid}',     hintKey: 'subid' },
  { label: '{sub_id}',    value: '{sub_id}',    hintKey: 'sub_id' },
  { label: '{SUBID}',     value: '{SUBID}',     hintKey: 'subidupper' },
  { label: '{aff_sub}',   value: '{aff_sub}',   hintKey: 'aff_sub' },
  { label: '{s1}',        value: '{s1}',        hintKey: 's1' },
];

export function AffiliatePostbackPanel({ pageId, embedded = false, hideEnableControl = false }: Props) {
  const { t } = useTranslation('builder');
  const [config, setConfig] = useState<AffiliateConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [postbackRows, setPostbackRows] = useState<PostbackRow[]>([]);
  const [showLog, setShowLog] = useState(false);

  // ── Phase 9: outbound tracker state ─────────────────────────────
  const [outbound, setOutbound] = useState<OutboundConfig | null>(null);
  const [outboundPresets, setOutboundPresets] = useState<OutboundPreset[]>([]);
  const [outboundSaving, setOutboundSaving] = useState(false);
  const [outboundExpanded, setOutboundExpanded] = useState(false);
  const [outboundTestResult, setOutboundTestResult] = useState<OutboundTestResult | null>(null);
  const [outboundTestRunning, setOutboundTestRunning] = useState(false);
  const [outboundLog, setOutboundLog] = useState<OutboundLogRow[]>([]);
  const [outboundLogVisible, setOutboundLogVisible] = useState(false);
  const [outboundLogLoading, setOutboundLogLoading] = useState(false);

  // Initial load. Calling GET /affiliate-config also auto-generates
  // the per-page postback secret on first touch, so the URLs will be
  // populated immediately even if the user has never configured this.
  useEffect(() => {
    if (!pageId) return;
    void loadConfig();
  }, [pageId]);

  const authHeader = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  async function loadConfig() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_URL}/capi/affiliate-config/${pageId}`, {
        headers: authHeader(),
      });
      if (res.status === 404) {
        // Page doesn't exist in DB yet — most common cause is a brand-
        // new draft that hasn't been Saved/Published. Show a friendly
        // hint instead of "HTTP 404" which scares the operator.
        setError('NOT_SAVED');
        setConfig(null);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setError('AUTH');
        setConfig(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: AffiliateConfig = await res.json();
      setConfig(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function updateConfig(patch: Partial<AffiliateConfig> & { rotate?: boolean }) {
    if (!pageId) return;
    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${API_URL}/capi/affiliate-config/${pageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Reload so we get fresh paste-ready URLs (especially after a
      // rotate, where the secret in URLs needs to update).
      await loadConfig();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function loadPostbacks() {
    try {
      const res = await fetch(`${API_URL}/capi/postbacks/${pageId}`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPostbackRows(data.rows || []);
    } catch (e) {
      console.warn('Failed to load postbacks:', e);
    }
  }

  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }

  // ── Phase 9: outbound tracker handlers ───────────────────────────
  useEffect(() => {
    if (!pageId) return;
    void loadOutbound();
  }, [pageId]);

  async function loadOutbound() {
    try {
      const res = await fetch(`${API_URL}/capi/outbound-config/${pageId}`, {
        headers: authHeader(),
      });
      // 403 here means the operator is on a tier without
      // affiliatePostback — silently skip. The inbound section already
      // shows the upgrade gate; we don't need a second.
      if (res.status === 403 || res.status === 404) return;
      if (!res.ok) return;
      const data = await res.json();
      setOutbound(data.outbound as OutboundConfig);
      setOutboundPresets((data.presets || []) as OutboundPreset[]);
    } catch (e) {
      console.warn('Failed to load outbound config:', e);
    }
  }

  async function saveOutbound(patch: Partial<OutboundConfig>) {
    if (!pageId) return;
    try {
      setOutboundSaving(true);
      const res = await fetch(`${API_URL}/capi/outbound-config/${pageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data?.outbound) setOutbound(data.outbound as OutboundConfig);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOutboundSaving(false);
    }
  }

  async function fireOutboundTest() {
    if (!pageId || !outbound) return;
    try {
      setOutboundTestRunning(true);
      setOutboundTestResult(null);
      const res = await fetch(`${API_URL}/capi/outbound-test/${pageId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        setOutboundTestResult({
          ok: false,
          url: data?.url || '',
          httpStatus: data?.httpStatus ?? null,
          body: data?.body ?? null,
          durationMs: 0,
          error: data?.error || `HTTP ${res.status}`,
        });
        return;
      }
      setOutboundTestResult(data as OutboundTestResult);
    } catch (e) {
      setOutboundTestResult({
        ok: false,
        url: '',
        httpStatus: null,
        body: null,
        durationMs: 0,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setOutboundTestRunning(false);
    }
  }

  async function loadOutboundLog() {
    if (!pageId) return;
    try {
      setOutboundLogLoading(true);
      const res = await fetch(`${API_URL}/capi/outbound-log/${pageId}?limit=50`, {
        headers: authHeader(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setOutboundLog((data.rows || []) as OutboundLogRow[]);
    } catch (e) {
      console.warn('Failed to load outbound log:', e);
    } finally {
      setOutboundLogLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="text-sm text-gray-500 italic">{t('affiliatePostback.loading')}</div>
    );
  }

  if (!config) {
    if (error === 'NOT_SAVED') {
      return (
        <div className="space-y-3">
          {!embedded && (
            <h4 className="text-base font-semibold text-white flex items-center gap-2">
              {t('affiliatePostback.notSaved.title')}
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-500/20 text-purple-300 uppercase tracking-wide">
                {t('affiliatePostback.capiBadge')}
              </span>
            </h4>
          )}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-xs text-blue-200">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              <Trans
                i18nKey="affiliatePostback.notSaved.line1"
                ns="builder"
                components={{ strong: <strong /> }}
              />
              <br />
              <Trans
                i18nKey="affiliatePostback.notSaved.line2"
                ns="builder"
                components={{ strong: <strong /> }}
              />
              <br />
              <span className="text-blue-300/60 text-[10px]">
                <Trans
                  i18nKey="affiliatePostback.notSaved.pageId"
                  ns="builder"
                  values={{ id: pageId }}
                  components={{ code: <code className="text-blue-200" /> }}
                />
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (error === 'AUTH') {
      return (
        <div className="text-sm text-red-400">
          {t('affiliatePostback.sessionExpired')}
        </div>
      );
    }
    return (
      <div className="text-sm text-red-400">
        {t('affiliatePostback.loadFailed')} {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideEnableControl && (
        <div className="flex items-start justify-between gap-4">
          {embedded ? (
            // Wrapper provides the title; show only the enable label so the
            // switch keeps a clear meaning.
            <div>
              <span className="text-sm font-medium text-gray-300">
                {t('affiliatePostback.enableEmbedded.label')}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('affiliatePostback.enableEmbedded.offHint')}
              </p>
            </div>
          ) : (
            <div>
              <h4 className="text-base font-semibold text-white flex items-center gap-2">
                {t('affiliatePostback.header.title')}
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-500/20 text-purple-300 uppercase tracking-wide">
                  {t('affiliatePostback.capiBadge')}
                </span>
              </h4>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                <Trans
                  i18nKey="affiliatePostback.header.description"
                  ns="builder"
                  components={{
                    reg: <strong className="text-gray-200" />,
                    ftd: <strong className="text-gray-200" />,
                  }}
                />
              </p>
            </div>
          )}
          <ToggleSwitch
            checked={config.enabled}
            onChange={(v) => void updateConfig({ enabled: v })}
            disabled={saving}
          />
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!config.enabled && (
        <div className="p-3 rounded-lg bg-gray-800/40 border border-gray-700/50 text-xs text-gray-400">
          {t('affiliatePostback.enablePanel')}
        </div>
      )}

      {config.enabled && config.postbackUrls && (
        <>
          {/* ─────────────── How it works ─────────────── */}
          <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20 space-y-2 text-xs text-gray-300 leading-relaxed">
            <div className="flex items-center gap-2 text-blue-300 font-semibold">
              <span>{t('affiliatePostback.howItWorks.title')}</span>
            </div>
            <ol className="list-decimal list-inside space-y-1 text-gray-400">
              <li>
                <Trans
                  i18nKey="affiliatePostback.howItWorks.step1"
                  ns="builder"
                  values={{ placeholder: config.clickIdPlaceholder }}
                  components={{ ph: <code className="px-1.5 py-0.5 bg-gray-900 rounded text-purple-300" /> }}
                />
              </li>
              <li>
                {t('affiliatePostback.howItWorks.step2')}
              </li>
              <li>
                {t('affiliatePostback.howItWorks.step3')}
              </li>
            </ol>
          </div>

          {/* ─────────────── Postback URLs ─────────────── */}
          <div className="space-y-3">
            <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
              {t('affiliatePostback.urlsHeading')}
            </div>

            <PostbackUrlRow
              label={t('affiliatePostback.urlReg')}
              accent="green"
              url={config.postbackUrls.registration}
              copied={copiedKey === 'reg'}
              onCopy={() => copyToClipboard(config.postbackUrls!.registration, 'reg')}
            />
            <PostbackUrlRow
              label={t('affiliatePostback.urlFtd')}
              accent="purple"
              url={config.postbackUrls.ftd}
              copied={copiedKey === 'ftd'}
              onCopy={() => copyToClipboard(config.postbackUrls!.ftd, 'ftd')}
            />

            <details className="group">
              <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-300">
                {t('affiliatePostback.showAdvanced')}
              </summary>
              <div className="mt-2">
                <PostbackUrlRow
                  label={t('affiliatePostback.urlCustom')}
                  accent="gray"
                  url={config.postbackUrls.custom}
                  copied={copiedKey === 'custom'}
                  onCopy={() => copyToClipboard(config.postbackUrls!.custom, 'custom')}
                />
                <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                  <Trans
                    i18nKey="affiliatePostback.customEventHint"
                    ns="builder"
                    components={{ code: <code className="text-gray-300" /> }}
                  />
                </p>
              </div>
            </details>

            <p className="text-[11px] text-gray-500 leading-relaxed">
              {t('affiliatePostback.macroNote')}
            </p>
          </div>

          {/* ─────────────── Click-ID Placeholder ─────────────── */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
              {t('affiliatePostback.clickIdHeading')}
            </div>
            <p className="text-[11px] text-gray-500">
              {t('affiliatePostback.clickIdDescription')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {PLACEHOLDER_PRESETS.map((preset) => {
                const active = config.clickIdPlaceholder === preset.value;
                return (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => void updateConfig({ clickIdPlaceholder: preset.value })}
                    className={`px-2.5 py-1 text-xs rounded border transition ${
                      active
                        ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                        : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                    }`}
                    title={t(`affiliatePostback.placeholders.${preset.hintKey}.hint` as any)}
                  >
                    <code>{preset.label}</code>
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-gray-500">
              <Trans
                i18nKey="affiliatePostback.activeLine"
                ns="builder"
                values={{ value: config.clickIdPlaceholder }}
                components={{ code: <code className="text-purple-300" /> }}
              />
              {PLACEHOLDER_PRESETS.find((p) => p.value === config.clickIdPlaceholder) && (
                <span className="text-gray-600">
                  ·{' '}
                  {t(
                    `affiliatePostback.placeholders.${PLACEHOLDER_PRESETS.find((p) => p.value === config.clickIdPlaceholder)!.hintKey}.hint` as any
                  )}
                </span>
              )}
            </div>
          </div>

          {/* ─────────────── Defaults ─────────────── */}
          <div className="grid grid-cols-3 gap-3">
            <NumberField
              label={t('affiliatePostback.defaults.regValue')}
              value={config.defaultRegistrationValue}
              onCommit={(n) => void updateConfig({ defaultRegistrationValue: n })}
              hint={t('affiliatePostback.defaults.regHint')}
            />
            <NumberField
              label={t('affiliatePostback.defaults.ftdValue')}
              value={config.defaultPurchaseValue}
              onCommit={(n) => void updateConfig({ defaultPurchaseValue: n })}
              hint={t('affiliatePostback.defaults.ftdHint')}
            />
            <TextField
              label={t('affiliatePostback.defaults.currency')}
              value={config.defaultCurrency}
              onCommit={(v) => void updateConfig({ defaultCurrency: v })}
              maxLength={3}
              hint={t('affiliatePostback.defaults.currencyHint')}
            />
          </div>

          {/* ─────────────── Footer actions ─────────────── */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
            <button
              type="button"
              onClick={() => {
                setShowLog((s) => !s);
                if (!showLog) void loadPostbacks();
              }}
              className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1.5"
            >
              <ExternalLink size={12} />
              {showLog ? t('affiliatePostback.log.hide') : t('affiliatePostback.log.show')}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                if (!confirm(t('affiliatePostback.rotate.confirm'))) return;
                void updateConfig({ rotate: true });
              }}
              className="text-xs text-gray-400 hover:text-red-300 inline-flex items-center gap-1.5"
              title={t('affiliatePostback.rotate.title')}
            >
              <RefreshCcw size={12} />
              {t('affiliatePostback.rotate.button')}
            </button>
          </div>

          {showLog && (
            <div className="space-y-1.5">
              {postbackRows.length === 0 ? (
                <div className="text-xs text-gray-500 italic p-3 bg-gray-800/30 rounded">
                  {t('affiliatePostback.noPostbacks')}
                </div>
              ) : (
                postbackRows.slice(0, 10).map((row) => (
                  <div
                    key={row.clickId}
                    className="flex items-center gap-2 text-xs p-2 bg-gray-800/40 rounded border border-gray-700/30"
                  >
                    {row.latest.capiOk ? (
                      <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle size={14} className="text-yellow-400 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-200">{row.latest.event}</span>
                    {row.latest.value != null && (
                      <span className="text-gray-400">
                        {row.latest.value} {row.latest.currency}
                      </span>
                    )}
                    <span className="text-gray-500 ml-auto">
                      {new Date(row.latest.receivedAt).toLocaleString()}
                    </span>
                    <code className="text-[10px] text-gray-600">{row.clickId.slice(0, 8)}</code>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ─────────────── Phase 9: Outbound tracker ─────────────── */}
          {outbound && (
            <OutboundSection
              outbound={outbound}
              presets={outboundPresets}
              expanded={outboundExpanded}
              onToggleExpand={() => setOutboundExpanded((v) => !v)}
              saving={outboundSaving}
              onSave={saveOutbound}
              onTest={fireOutboundTest}
              testRunning={outboundTestRunning}
              testResult={outboundTestResult}
              logVisible={outboundLogVisible}
              onToggleLog={() => {
                const next = !outboundLogVisible;
                setOutboundLogVisible(next);
                if (next) void loadOutboundLog();
              }}
              logLoading={outboundLogLoading}
              logRows={outboundLog}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Phase 9: Outbound section (Voluum / RedTrack / Binom / FunnelFlux / Bemob)
// ─────────────────────────────────────────────────────────────────────

interface OutboundSectionProps {
  outbound: OutboundConfig;
  presets: OutboundPreset[];
  expanded: boolean;
  onToggleExpand: () => void;
  saving: boolean;
  onSave: (patch: Partial<OutboundConfig>) => Promise<void>;
  onTest: () => Promise<void>;
  testRunning: boolean;
  testResult: OutboundTestResult | null;
  logVisible: boolean;
  onToggleLog: () => void;
  logLoading: boolean;
  logRows: OutboundLogRow[];
}

function OutboundSection({
  outbound,
  presets,
  expanded,
  onToggleExpand,
  saving,
  onSave,
  onTest,
  testRunning,
  testResult,
  logVisible,
  onToggleLog,
  logLoading,
  logRows,
}: OutboundSectionProps) {
  const { t } = useTranslation('builder');

  const activePreset = useMemo(
    () => presets.find((p) => p.id === outbound.preset) || null,
    [presets, outbound.preset]
  );
  const isCustom = outbound.preset === 'custom';

  // Live preview — substitute sample values into the current template
  // so the operator sees what will actually fire BEFORE saving. Mirrors
  // the server's substituteMacros() but cheaper to run client-side
  // for a preview-only string.
  const livePreview = useMemo(() => {
    const template =
      outbound.postbackUrlTemplate || activePreset?.postbackUrlTemplate || '';
    const ctx: Record<string, string | number> = {
      trackerDomain: outbound.trackerDomain || 'tracker.example.com',
      clickid: 'TEST-CLICK-ID',
      payout: outbound.defaultPayout || 1.23,
      currency: outbound.defaultCurrency || 'USD',
      txid: 'TX-PREVIEW',
      event: 'CompleteRegistration',
      event_name: 'CompleteRegistration',
      hitzlink_click_id: 'HZ-PREVIEW',
      ...outbound.macroOverrides,
    };
    let url = template;
    for (const [k, v] of Object.entries(ctx)) {
      const val = encodeURIComponent(String(v ?? ''));
      url = url.replace(new RegExp(`\\{${k}\\}`, 'gi'), val);
    }
    return url;
  }, [outbound, activePreset]);

  return (
    <div className="pt-4 mt-2 border-t border-gray-700/40 space-y-3">
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div>
          <h4 className="text-base font-semibold text-white flex items-center gap-2">
            {t('affiliatePostback.outbound.title')}
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-500/20 text-orange-300 uppercase tracking-wide">
              {t('affiliatePostback.outbound.badge')}
            </span>
          </h4>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">
            {t('affiliatePostback.outbound.description')}
          </p>
        </div>
        <span className="text-xs text-gray-500">{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">
              {t('affiliatePostback.outbound.enabled')}
            </span>
            <ToggleSwitch
              checked={outbound.enabled}
              onChange={(v) => void onSave({ enabled: v })}
              disabled={saving}
            />
          </div>

          {outbound.enabled && (
            <>
              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">
                  {t('affiliatePostback.outbound.preset')}
                </label>
                <select
                  value={outbound.preset}
                  disabled={saving}
                  onChange={(e) => {
                    const presetId = e.target.value;
                    const next = presets.find((p) => p.id === presetId);
                    void onSave({
                      preset: presetId,
                      // Preload the preset's template when switching
                      // away from custom; preserve when going TO custom
                      // so the operator can edit what was there.
                      postbackUrlTemplate:
                        presetId === 'custom'
                          ? outbound.postbackUrlTemplate
                          : next?.postbackUrlTemplate || '',
                    });
                  }}
                  className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                >
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
                {activePreset?.notes && (
                  <p className="text-[10px] text-gray-500 mt-1">{activePreset.notes}</p>
                )}
              </div>

              <TextField
                label={t('affiliatePostback.outbound.trackerDomain')}
                value={outbound.trackerDomain}
                onCommit={(v) => void onSave({ trackerDomain: v.toLowerCase() })}
                hint={t('affiliatePostback.outbound.trackerDomainHint')}
              />

              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">
                  {t('affiliatePostback.outbound.urlTemplate')}
                </label>
                <UrlTemplateField
                  value={outbound.postbackUrlTemplate}
                  editable={isCustom}
                  onCommit={(v) => void onSave({ postbackUrlTemplate: v })}
                />
                {!isCustom && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    {t('affiliatePostback.outbound.urlTemplateLockedHint')}
                  </p>
                )}
              </div>

              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">
                  {t('affiliatePostback.outbound.urlPreview')}
                </label>
                <code className="block text-[11px] text-orange-200 font-mono break-all leading-snug select-all p-2 bg-gray-900/60 border border-orange-500/20 rounded">
                  {livePreview || '—'}
                </code>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  label={t('affiliatePostback.outbound.defaultPayout')}
                  value={outbound.defaultPayout}
                  onCommit={(n) => void onSave({ defaultPayout: n })}
                  hint={t('affiliatePostback.outbound.defaultPayoutHint')}
                />
                <TextField
                  label={t('affiliatePostback.outbound.defaultCurrency')}
                  value={outbound.defaultCurrency}
                  onCommit={(v) => void onSave({ defaultCurrency: v })}
                  maxLength={3}
                  hint={t('affiliatePostback.outbound.defaultCurrencyHint')}
                />
              </div>

              <details className="group">
                <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-300">
                  {t('affiliatePostback.outbound.macroOverrides')}
                </summary>
                <div className="mt-2 space-y-2">
                  <p className="text-[10px] text-gray-500">
                    {t('affiliatePostback.outbound.macroOverridesHint')}
                  </p>
                  <MacroOverridesEditor
                    value={outbound.macroOverrides}
                    onCommit={(next) => void onSave({ macroOverrides: next })}
                  />
                </div>
              </details>

              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">
                  {t('affiliatePostback.outbound.eventsToForward')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {OUTBOUND_EVENT_KEYS.map(({ key, labelKey }) => {
                    const checked = outbound.eventsEnabled[key] !== false;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          void onSave({
                            eventsEnabled: {
                              ...outbound.eventsEnabled,
                              [key]: !checked,
                            },
                          })
                        }
                        className={`px-2.5 py-1 text-xs rounded border transition ${
                          checked
                            ? 'bg-orange-500/20 border-orange-500/50 text-orange-200'
                            : 'bg-gray-800/50 border-gray-700/50 text-gray-500'
                        }`}
                      >
                        {t(`affiliatePostback.outbound.events.${labelKey}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-700/30">
                <button
                  type="button"
                  onClick={onToggleLog}
                  className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1.5"
                >
                  <ListChecks size={12} />
                  {logVisible
                    ? t('affiliatePostback.outbound.hideLog')
                    : t('affiliatePostback.outbound.viewLog')}
                </button>
                <button
                  type="button"
                  onClick={() => void onTest()}
                  disabled={testRunning || saving}
                  className="text-xs px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 text-orange-200 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Send size={12} />
                  {testRunning
                    ? t('affiliatePostback.outbound.testRunning')
                    : t('affiliatePostback.outbound.testButton')}
                </button>
              </div>

              {testResult && (
                <div
                  className={`p-3 rounded-lg text-xs space-y-1.5 ${
                    testResult.ok
                      ? 'bg-green-500/10 border border-green-500/30 text-green-200'
                      : 'bg-red-500/10 border border-red-500/30 text-red-200'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-1.5">
                    {testResult.ok ? (
                      <>
                        <CheckCircle2 size={14} />
                        {t('affiliatePostback.outbound.testSuccess', {
                          status: testResult.httpStatus ?? '?',
                          duration: testResult.durationMs,
                        })}
                      </>
                    ) : (
                      <>
                        <AlertCircle size={14} />
                        {t('affiliatePostback.outbound.testFailed', {
                          error: testResult.error || `HTTP ${testResult.httpStatus ?? '?'}`,
                        })}
                      </>
                    )}
                  </div>
                  {testResult.url && (
                    <code className="block break-all opacity-80 text-[10px]">
                      {testResult.url}
                    </code>
                  )}
                  {testResult.body && (
                    <code className="block break-all opacity-60 text-[10px] mt-1">
                      {testResult.body.slice(0, 200)}
                    </code>
                  )}
                </div>
              )}

              {logVisible && (
                <div className="space-y-1.5">
                  {logLoading ? (
                    <div className="text-xs text-gray-500 italic p-3">
                      {t('affiliatePostback.outbound.logLoading')}
                    </div>
                  ) : logRows.length === 0 ? (
                    <div className="text-xs text-gray-500 italic p-3 bg-gray-800/30 rounded">
                      {t('affiliatePostback.outbound.logEmpty')}
                    </div>
                  ) : (
                    logRows.slice(0, 50).map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center gap-2 text-xs p-2 bg-gray-800/40 rounded border border-gray-700/30"
                      >
                        <StatusIcon status={row.status} />
                        <span className="font-medium text-gray-200 w-28 truncate">
                          {row.event_name}
                        </span>
                        <span className="text-gray-400 w-12 text-center">
                          {row.http_status ?? '—'}
                        </span>
                        <span className="text-gray-500 w-14 text-right">
                          {row.duration_ms != null ? `${row.duration_ms}ms` : '—'}
                        </span>
                        <span className="text-gray-500 w-8 text-right">
                          ×{row.attempt_count}
                        </span>
                        <span className="text-gray-500 ml-auto truncate max-w-[180px]">
                          {new Date(row.created_at).toLocaleString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: OutboundLogRow['status'] }) {
  switch (status) {
    case 'success':
      return <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />;
    case 'failed':
      return <AlertCircle size={14} className="text-red-400 flex-shrink-0" />;
    case 'skipped':
      return <ExternalLink size={14} className="text-gray-500 flex-shrink-0" />;
    case 'pending':
      return <RefreshCcw size={14} className="text-yellow-400 flex-shrink-0" />;
    default: {
      // Exhaustiveness — TypeScript should narrow `status` to never here.
      const _exhaustive: never = status;
      void _exhaustive;
      return <AlertCircle size={14} className="text-gray-500 flex-shrink-0" />;
    }
  }
}

function UrlTemplateField({
  value,
  editable,
  onCommit,
}: {
  value: string;
  editable: boolean;
  onCommit: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <textarea
      value={local}
      readOnly={!editable}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (!editable) return;
        if (local !== value) onCommit(local);
      }}
      rows={3}
      className={`w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs font-mono text-white focus:outline-none focus:ring-1 focus:ring-orange-500 ${
        !editable ? 'opacity-70 cursor-not-allowed' : ''
      }`}
    />
  );
}

function MacroOverridesEditor({
  value,
  onCommit,
}: {
  value: Record<string, string>;
  onCommit: (next: Record<string, string>) => void;
}) {
  const [rows, setRows] = useState<Array<{ k: string; v: string }>>(
    Object.entries(value).map(([k, v]) => ({ k, v }))
  );
  useEffect(() => {
    setRows(Object.entries(value).map(([k, v]) => ({ k, v })));
  }, [value]);

  function commit(next: Array<{ k: string; v: string }>) {
    const obj: Record<string, string> = {};
    for (const r of next) {
      if (r.k.trim() && r.v.trim()) obj[r.k.trim()] = r.v.trim();
    }
    onCommit(obj);
  }

  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            type="text"
            value={row.k}
            onChange={(e) => {
              const next = rows.slice();
              next[i] = { ...next[i], k: e.target.value };
              setRows(next);
            }}
            onBlur={() => commit(rows)}
            placeholder="macro"
            className="flex-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          <span className="text-gray-500 text-xs">→</span>
          <input
            type="text"
            value={row.v}
            onChange={(e) => {
              const next = rows.slice();
              next[i] = { ...next[i], v: e.target.value };
              setRows(next);
            }}
            onBlur={() => commit(rows)}
            placeholder="value or ctx key"
            className="flex-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          <button
            type="button"
            onClick={() => {
              const next = rows.filter((_, j) => j !== i);
              setRows(next);
              commit(next);
            }}
            className="text-gray-500 hover:text-red-400 px-1.5 text-xs"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows([...rows, { k: '', v: '' }])}
        className="text-[11px] text-orange-300 hover:text-orange-200"
      >
        + add override
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────

function PostbackUrlRow({
  label,
  url,
  copied,
  onCopy,
  accent,
}: {
  label: string;
  url: string;
  copied: boolean;
  onCopy: () => void;
  accent: 'green' | 'purple' | 'gray';
}) {
  const { t } = useTranslation('builder');
  const accentClass = {
    green: 'border-green-500/30 bg-green-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
    gray: 'border-gray-700/50 bg-gray-800/30',
  }[accent];

  return (
    <div className={`p-3 rounded-lg border ${accentClass}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-200">{label}</span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded bg-gray-900/60 hover:bg-gray-900 text-gray-300 transition"
        >
          {copied ? (
            <>
              <CheckCircle2 size={12} className="text-green-400" />
              {t('affiliatePostback.copied')}
            </>
          ) : (
            <>
              <Copy size={12} />
              {t('affiliatePostback.copy')}
            </>
          )}
        </button>
      </div>
      <code className="block text-[11px] text-gray-300 font-mono break-all leading-snug select-all">
        {url}
      </code>
    </div>
  );
}

function NumberField({
  label,
  value,
  onCommit,
  hint,
}: {
  label: string;
  value: number;
  onCommit: (n: number) => void;
  hint?: string;
}) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => setLocal(String(value)), [value]);
  return (
    <div>
      <label className="text-[11px] font-medium text-gray-400 mb-1 block">{label}</label>
      <input
        type="number"
        min={0}
        step="0.01"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const n = Number(local);
          if (Number.isFinite(n) && n >= 0 && n !== value) onCommit(n);
        }}
        className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
      />
      {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function TextField({
  label,
  value,
  onCommit,
  maxLength,
  hint,
}: {
  label: string;
  value: string;
  onCommit: (v: string) => void;
  maxLength?: number;
  hint?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <div>
      <label className="text-[11px] font-medium text-gray-400 mb-1 block">{label}</label>
      <input
        type="text"
        maxLength={maxLength}
        value={local}
        onChange={(e) => setLocal(e.target.value.toUpperCase())}
        onBlur={() => {
          if (local !== value) onCommit(local);
        }}
        className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500 uppercase"
      />
      {hint && <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );
}
